import { useEffect, useState } from 'react';
import { ActionCard, ActionCardSkeleton } from './ActionCard';
import { WeeklyTimeSheet } from './WeeklyTimeSheet';
import { useAuth } from '../../contexts/AuthContext';
import { useChangeAnswer } from '../../hooks/useChangeAnswer';
import { useToast } from '../../hooks/useToast';
import { useUserIdentity } from '../../hooks/useUserIdentity';
import { useViewerZone } from '../../hooks/useViewerZone';
import { subscribeMembership } from '../../services/liveMemberships';
import { changeFromWeeklyProposal } from '../../utils/actionCard';
import { firstNameOf } from '../../utils/liveLessonText';
import { weeklyTimeToast } from '../../utils/oneToast';
import { proposalLines, weeklyLine } from '../../utils/weeklyProposal';
import type { Message } from '../../types/chat';
import type { LiveMembership } from '../../types/liveCall';

/**
 * The coach's weekly time suggestion in the thread as the Action card (decision 165, row 2; decision 96): the
 * old weekly time struck through, the new ones as rows, pick then "Confirm Thursdays 11:50 AM", and "Suggest a
 * Different Time", which opens the player's weekly time sheet. No Keep: not answering keeps it. The server
 * updates the message in place; a kept, picking or replaced one reads its own line.
 */
export function WeeklyProposalBubble({ message }: { message: Message }) {
  const p = message.weeklyProposal!;
  const { currentUser } = useAuth();
  const { showSuccess } = useToast();
  const tz = useViewerZone();
  const [m, setM] = useState<LiveMembership | null | undefined>(undefined);
  useEffect(() => subscribeMembership(p.membershipId, setM), [p.membershipId]);
  // The server posts it as System: the plan says who the coach is.
  const coach = useUserIdentity(m?.coachId, 'coach');
  const player = useUserIdentity(m?.playerId, 'player');
  const [picking, setPicking] = useState(false);
  const [now] = useState(() => Date.now());
  const isCoach = !!m && currentUser?.uid === m.coachId;
  const coachFirst = firstNameOf(coach?.name ?? m?.coachName, 'Your coach');
  const playerFirst = firstNameOf(player?.name ?? (m as { playerName?: string } | null | undefined)?.playerName, 'Your player');
  const { line } = proposalLines({ status: p.status, isCoach, coachFirst, playerFirst });
  // The weekly time it would replace, in the reader's zone, while there is one.
  const from = m?.weeklyTime && p.hasWeeklyTime ? weeklyLine(m.weeklyTime, tz, now) : undefined;
  const change = changeFromWeeklyProposal({ proposalId: p.proposalId, membershipId: p.membershipId, status: p.status, chosen: p.chosen,
    slotLabels: p.slots.map((s) => (isCoach ? s.coachLabel : s.label)) }, { coachFirst, playerFirst }, message.createdAt, from, line);
  const answer = useChangeAnswer({ tz, onSuggestDifferent: () => setPicking(true) });

  if (!m) return <div className="my-3 flex justify-center"><div className="w-full max-w-[320px]"><ActionCardSkeleton /></div></div>;
  return (
    <div className="my-3 flex justify-center" data-weekly-proposal={p.status}>
      <ActionCard change={change} me={isCoach ? 'coach' : 'player'} tz={tz} onAnswer={answer}
        className="w-full max-w-[320px] border border-[#e6e6e6]" />
      {picking && (
        <WeeklyTimeSheet membershipId={m.id} coachId={m.coachId} coachFirstName={coachFirst} tz={tz}
          initial={m.weeklyTime ? { weekday: m.weeklyTime.weekday, time: m.weeklyTime.time, lessonMode: m.weeklyTime.lessonMode } : null}
          onClose={(l) => { setPicking(false); if (l) showSuccess(weeklyTimeToast(l)); }} />
      )}
    </div>
  );
}
